// MosqueSelection.jsx
import { useCallback, useEffect, useRef, useState } from "react";
import UiMosque from "../../utils/icons/UiMosque";

import MosqueLocationSelector from "./MosqueLocationSelector";
import GenericCombobox from "../../components/deliveryLoctions/GenericCombobox";
import { getMosquesByAreaId } from "../../utils/SonicAPIV2";

export const MosqueSelection = ({ selectedMosque, setSelectedMosque }) => {
  // ---- Dialog visibility ----
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // ---- Persisted picker state (kept across open/close, only reset on explicit area change) ----
  const [location, setLocation] = useState({
    country: null,
    state: null,
    city: null,
    area: null,
  });
  const [mosqueData, setMosqueData] = useState([]);
  const [mosquesLoading, setMosquesLoading] = useState(false);
  const [pendingMosque, setPendingMosque] = useState(null);

  // prevents re-fetch if selector re-emits same areaId
  const lastLoadedAreaIdRef = useRef(null);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    // Just close — deliberately keep location/mosqueData/pendingMosque as-is
    // so the picker remembers where the user left off next time it opens.
    setIsDialogOpen(false);
  }, []);

  const handleLocationChange = useCallback((loc) => {
    setLocation(loc);

    // if area actually changed, the old mosque list/selection no longer applies
    const nextAreaId = loc?.area?.areaId ?? null;
    if (lastLoadedAreaIdRef.current !== nextAreaId) {
      setPendingMosque(null);
      setMosqueData([]);
    }
  }, []);

  // Load mosques when Area is selected/changed
  useEffect(() => {
    if (!isDialogOpen) return;
    let cancelled = false;

    (async () => {
      const areaId = location?.area?.areaId;
      if (!areaId) return;

      if (lastLoadedAreaIdRef.current === areaId) return;
      lastLoadedAreaIdRef.current = areaId;

      setMosquesLoading(true);
      try {
        const list = await getMosquesByAreaId(areaId);
        if (cancelled) return;

        setMosqueData(Array.isArray(list) ? list : []);
      } catch (err) {
        if (cancelled) return;

        console.error("[MosqueSelection] getMosquesByAreaId error:", err);
        setMosqueData([]);
        alert(
          "No Mosque Found! We are updating our Mosques List, please select other area"
        );
      } finally {
        if (!cancelled) setMosquesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location?.area?.areaId, isDialogOpen]);

  const handleConfirm = () => {
    if (!pendingMosque) return;

    setSelectedMosque?.({
      MosqueID: pendingMosque.CustomerCode,
      MosqueName: pendingMosque.Description,
    });

    setIsDialogOpen(false);
    // pendingMosque / location / mosqueData intentionally left as-is
  };

  return (
    <div className="mx-auto">
      <div className="text-left mb-2">
        <span className="text-base text-cool-gray-600 font-poppins font-normal">
          Select Nearest Mosque
        </span>
      </div>

      {/* ===================================== */}
      {/* TRIGGER: summary card OR "select" card */}
      {/* ===================================== */}
      {selectedMosque?.MosqueName ? (
        <div className="flex items-center justify-between bg-navbarBackground ring-1 ring-secondary rounded-md px-4 py-3">
          <div className="flex items-center gap-2">
            <UiMosque fillColor="#3F95A9" />
            <div>
              <div className="text-xs text-gray-500">Delivering to mosque</div>
              <div className="text-sm font-medium text-gray-900">
                {selectedMosque.MosqueName}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={openDialog}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-secondary hover:bg-primaryLight text-textWhite hover:text-textBlack border border-primary rounded-md transition"
          >
            {/* ICON SLOT: e.g. edit/pencil icon */}
            <span className="w-4 h-4 inline-block" />
            Change Mosque
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={openDialog}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:border-primary hover:text-primary transition"
        >
          {/* ICON SLOT: e.g. mosque/plus icon */}
          <span className="w-5 h-5 inline-block" />
          Select Mosque
        </button>
      )}

      {/* ===================================== */}
      {/* DIALOG: mosque picker */}
      {/* ===================================== */}
      {isDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-primaryVeryLight rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 rounded-full p-3 mr-4">
                <UiMosque fillColor="#2563eb" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Select Nearest Mosque
              </h3>
            </div>

            <div className="mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="mt-2 md:col-span-2">
                <MosqueLocationSelector
                  initialArea={location?.area}
                  onChange={handleLocationChange}
                />
              </div>

              <div className="mt-2 md:col-span-2">
                <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-md sm:rounded-r-none sm:rounded-l-md justify-start items-center gap-1 inline-flex shrink-0 w-full">
                  <UiMosque fillColor="black" />
                  <div className="font-normal text-C12 w-12">Mosque</div>

                  <GenericCombobox
                    label=""
                    options={mosqueData}
                    selected={pendingMosque}
                    onChange={setPendingMosque}
                    displayKey="Description"
                    keyField="CustomerCode"
                    disabled={!location?.area || mosquesLoading}
                  />
                </div>
              </div>

              <div className="md:col-span-2 mt-1 text-xs text-gray-600">
                {!location?.area ? "Select an area to load mosques." : null}
                {mosquesLoading ? " Loading mosques..." : null}
                {!mosquesLoading && location?.area && mosqueData.length === 0
                  ? " No mosques found for this area."
                  : null}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={closeDialog}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 hover:bg-darkVeryLight rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
              >
                {/* ICON SLOT: e.g. x icon */}
                <span className="w-4 h-4 inline-block" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!pendingMosque}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-transparent bg-primary hover:bg-primaryHover text-base font-medium hover:text-textBlack text-textWhite shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {/* ICON SLOT: e.g. check icon */}
                <span className="w-4 h-4 inline-block" />
                Confirm Mosque
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MosqueSelection;
